import React from 'react'
import { Line } from 'react-chartjs-2'
import { ChartOptions } from 'chart.js'
import { ChartContainer, ChartTitle } from './ModelTrainingView.styles'
import { Model } from '../../../types/models/model'
import { TrainingJobEntryBBox } from '../../../types/models/model'

interface BBoxPerformanceMetricsChartProps {
  model: Model | undefined
  height?: string
}

const BBoxPerformanceMetricsChart: React.FC<BBoxPerformanceMetricsChartProps> = ({
  model,
  height = '225px',
}) => {
  const metricsChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#ffffff',
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label || ''
            const value = context.raw
            const displayValue =
              context.dataset.originalData?.[context.dataIndex] === null ? 'null' : value.toFixed(4)
            return `${label}: ${displayValue}`
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#ffffff',
          autoSkip: true,
          callback: function (
            this: any,
            tickValue: string | number,
            index: number,
            ticks: any[]
          ): string | null {
            if (index === 0 || index === ticks.length - 1) {
              return this.getLabelForValue(tickValue)
            }
            if (index % Math.ceil(ticks.length / 10) === 0) {
              return this.getLabelForValue(tickValue)
            }
            return null
          },
          maxRotation: 45,
          minRotation: 45,
          includeBounds: true,
        },
        afterFit: (scale: any) => {
          scale.paddingRight = 20
        },
      },
      y: {
        type: 'linear' as const,
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#ffffff',
        },
      },
    },
  }

  const getMetricsChartData = (model: Model | undefined) => {
    if (!model?.training_stats?.bounding_box?.training_job_metrics) {
      return {
        labels: [],
        datasets: [],
      }
    }

    const firstJobKey = Object.keys(
      model.training_stats?.bounding_box?.training_job_metrics || {}
    )[0]
    if (!firstJobKey) {
      return { labels: [], datasets: [] }
    }

    const jobEntries =
      model.training_stats?.bounding_box?.training_job_metrics[firstJobKey].training_job_entries

    return {
      labels: jobEntries.map((entry: TrainingJobEntryBBox) => `Epoch ${(entry.epoch || 0) + 1}`),
      datasets: [
        {
          label: 'Precision',
          data: jobEntries.map(
            (entry: TrainingJobEntryBBox) => (entry.metrics_precision || 0) * 100
          ),
          originalData: jobEntries.map(
            (entry: TrainingJobEntryBBox) => entry.metrics_precision || 0
          ),
          borderColor: 'rgba(13, 202, 240, 1)',
          backgroundColor: 'rgba(13, 202, 240, 0.2)',
          tension: 0.4,
        },
        {
          label: 'Recall',
          data: jobEntries.map((entry: TrainingJobEntryBBox) => (entry.metrics_recall || 0) * 100),
          originalData: jobEntries.map((entry: TrainingJobEntryBBox) => entry.metrics_recall || 0),
          borderColor: 'rgba(25, 135, 84, 1)',
          backgroundColor: 'rgba(25, 135, 84, 0.2)',
          tension: 0.4,
        },
        {
          label: 'mAP50',
          data: jobEntries.map((entry: TrainingJobEntryBBox) => (entry.metrics_map_50 || 0) * 100),
          originalData: jobEntries.map((entry: TrainingJobEntryBBox) => entry.metrics_map_50 || 0),
          borderColor: 'rgba(220, 53, 69, 1)',
          backgroundColor: 'rgba(220, 53, 69, 0.2)',
          tension: 0.4,
        },
        {
          label: 'mAP50-95',
          data: jobEntries.map(
            (entry: TrainingJobEntryBBox) => (entry.metrics_map_50_95 || 0) * 100
          ),
          originalData: jobEntries.map(
            (entry: TrainingJobEntryBBox) => entry.metrics_map_50_95 || 0
          ),
          borderColor: 'rgba(255, 193, 7, 1)',
          backgroundColor: 'rgba(255, 193, 7, 0.2)',
          tension: 0.4,
        },
      ],
    }
  }

  if (!model) {
    return <div>No training data available</div>
  }

  return (
    <ChartContainer>
      <ChartTitle>Performance Metrics</ChartTitle>
      <div style={{ width: '100%', height }}>
        <Line data={getMetricsChartData(model)} options={metricsChartOptions} />
      </div>
    </ChartContainer>
  )
}

export default BBoxPerformanceMetricsChart
